/**
 * Tiny observable store with localStorage persistence.
 *
 * Everything lives on the client — there is no backend and no account, so the
 * store *is* the database. Keys are namespaced and versioned so a schema change
 * can migrate rather than blow away someone's watchlist.
 */

import type { ApiKeyEntry, RankedWatch, Settings, WatchItem } from "./types.ts";

export const STORAGE_PREFIX = "sniper.v1.";

export const STORAGE_KEYS = {
  settings: `${STORAGE_PREFIX}settings`,
  keys: `${STORAGE_PREFIX}keys`,
  watchlist: `${STORAGE_PREFIX}watchlist`,
  ranked: `${STORAGE_PREFIX}ranked`,
  ignored: `${STORAGE_PREFIX}ignored`,
} as const;

export const DEFAULT_SETTINGS: Settings = {
  profitPct: 20,
  intervalSec: 30,
  soundEnabled: true,
  notifEnabled: true,
  bazaarEnabled: true,
  bazaarTickRatio: 2,
  bazaarVerify: true,
  onlyUnderMv: false,
  minUnits: 1,
  marketFeeEnabled: true,
  anonFeeEnabled: false,
  theme: "auto",
};

type Listener<T> = (value: T) => void;

export class Observable<T> {
  private listeners = new Set<Listener<T>>();

  constructor(private value: T) {}

  get(): T {
    return this.value;
  }

  set(next: T): void {
    this.value = next;
    for (const listener of this.listeners) listener(next);
  }

  update(fn: (current: T) => T): void {
    this.set(fn(this.value));
  }

  /** Re-emit without changing the reference, for in-place mutations. */
  notify(): void {
    for (const listener of this.listeners) listener(this.value);
  }

  subscribe(listener: Listener<T>, immediate = true): () => void {
    this.listeners.add(listener);
    if (immediate) listener(this.value);
    return () => this.listeners.delete(listener);
  }
}

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    if (raw === null) return fallback;
    const parsed = JSON.parse(raw) as T;
    return parsed ?? fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    // Quota or a locked-down browser. Non-fatal: the session keeps working,
    // it just will not survive a reload.
    console.warn(`[sniper] could not persist ${key}`, err);
  }
}

/** An Observable that mirrors itself into localStorage on every change. */
export class PersistedObservable<T> extends Observable<T> {
  constructor(
    private readonly storageKey: string,
    fallback: T,
    hydrate: (raw: T) => T = (raw) => raw,
  ) {
    super(hydrate(readJson(storageKey, fallback)));
    this.subscribe((value) => writeJson(this.storageKey, value), false);
  }
}

/** Settings gain new fields over time; merge so old saves keep working. */
const hydrateSettings = (raw: Settings): Settings => ({ ...DEFAULT_SETTINGS, ...raw });

const hydrateWatchlist = (raw: WatchItem[]): WatchItem[] =>
  (Array.isArray(raw) ? raw : []).map((item) => ({
    ...item,
    maxBuyPrice: item.maxBuyPrice ?? null,
    minUnits: item.minUnits ?? null,
    intervalSec: item.intervalSec ?? null,
    addedAt: item.addedAt ?? Date.now(),
  }));

const hydrateKeys = (raw: ApiKeyEntry[]): ApiKeyEntry[] =>
  (Array.isArray(raw) ? raw : []).map((entry) => ({
    ...entry,
    // Health is a runtime concern; never trust a persisted "valid".
    health: "unknown" as const,
    cooldownUntil: null,
    lastError: null,
    lastUsedAt: null,
  }));

export const settings$ = new PersistedObservable<Settings>(
  STORAGE_KEYS.settings,
  DEFAULT_SETTINGS,
  hydrateSettings,
);

export const keys$ = new PersistedObservable<ApiKeyEntry[]>(STORAGE_KEYS.keys, [], hydrateKeys);

export const watchlist$ = new PersistedObservable<WatchItem[]>(
  STORAGE_KEYS.watchlist,
  [],
  hydrateWatchlist,
);

export const ranked$ = new PersistedObservable<RankedWatch[]>(STORAGE_KEYS.ranked, []);

/** Offer keys the user has dismissed; they never alert again this install. */
export const ignored$ = new PersistedObservable<string[]>(STORAGE_KEYS.ignored, []);

export function patchSettings(patch: Partial<Settings>): void {
  settings$.update((current) => ({ ...current, ...patch }));
}

export function clearAllStorage(): void {
  for (const key of Object.values(STORAGE_KEYS)) {
    try {
      localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }
}
